/**
 * crystal.js — 時光晶核 / 星球 Mesh
 *
 * Level visual mapping (mirrors GAME_LEVEL_THRESHOLDS = [0, 200, 800, 3000]):
 *
 *  Lv 1 — 晶核萌發:  IcosahedronGeometry(detail=0), flat purple, wireframe glow
 *  Lv 2 — 數據地貌:  IcosahedronGeometry(detail=1), metallic sheen, vertex displacement
 *  Lv 3 — 時光星球:  SphereGeometry + normal map simulation, teal/blue palette
 *  Lv 4 — 星系航跡:  Sphere + orbital ring + particle halo, gold accent
 *
 * Public API:
 *   createCrystal(level)  → adds mesh group to scene, returns { group, update, pulse }
 *   pulse()               → triggers a scale-spike animation (called after upgrade)
 *   update(t)             → called every frame (rotation, breathing)
 */

import * as THREE from 'three'
import { scene } from './scene.js'

// ── Level colour palette ──────────────────────────────────────────────────────
const LEVEL_COLORS = {
  1: { main: 0x7c6af7, emissive: 0x2a1a8a, wireframe: 0x9b8fff },
  2: { main: 0x5b9cf6, emissive: 0x0d3070, wireframe: 0x80c0ff },
  3: { main: 0x4bc8a0, emissive: 0x0a3d2a, wireframe: 0x70eec0 },
  4: { main: 0xf0a500, emissive: 0x5a3a00, wireframe: 0xffd060 },
}

// ── Internal state ────────────────────────────────────────────────────────────
let _group = null // THREE.Group containing all meshes
let _coreMesh = null // main solid mesh
let _wireMesh = null // wireframe overlay (glow effect)
let _ring = null // orbital ring (Lv 4 only)
let _particles = null // particle halo (Lv 3+)

let _pulseT = -999 // timestamp of last pulse trigger
const PULSE_DURATION = 0.6 // seconds

// ── Geometry factory ──────────────────────────────────────────────────────────
function _makeGeometry(level) {
  switch (level) {
    case 1:
      return new THREE.IcosahedronGeometry(1.0, 0) // flat, 20 faces
    case 2:
      return new THREE.IcosahedronGeometry(1.1, 1) // 80 faces, more detail
    case 3:
      return new THREE.SphereGeometry(1.15, 32, 24) // smooth sphere
    case 4:
      return new THREE.SphereGeometry(1.2, 40, 32)
    default:
      return new THREE.IcosahedronGeometry(1.0, 0)
  }
}

// ── Material factory ──────────────────────────────────────────────────────────
function _makeMaterial(level) {
  const c = LEVEL_COLORS[level] ?? LEVEL_COLORS[1]
  switch (level) {
    case 1:
      return new THREE.MeshPhongMaterial({
        color: c.main,
        emissive: c.emissive,
        shininess: 60,
        flatShading: true,
      })
    case 2:
      return new THREE.MeshStandardMaterial({
        color: c.main,
        emissive: c.emissive,
        emissiveIntensity: 0.3,
        metalness: 0.4,
        roughness: 0.5,
        flatShading: true,
      })
    case 3:
      return new THREE.MeshStandardMaterial({
        color: c.main,
        emissive: c.emissive,
        emissiveIntensity: 0.4,
        metalness: 0.2,
        roughness: 0.3,
      })
    case 4:
      return new THREE.MeshStandardMaterial({
        color: c.main,
        emissive: c.emissive,
        emissiveIntensity: 0.6,
        metalness: 0.6,
        roughness: 0.2,
      })
    default:
      return new THREE.MeshPhongMaterial({ color: c.main, flatShading: true })
  }
}

// ── Wireframe glow ────────────────────────────────────────────────────────────
function _makeWireframe(geo, level) {
  const c = LEVEL_COLORS[level] ?? LEVEL_COLORS[1]
  return new THREE.Mesh(
    geo,
    new THREE.MeshBasicMaterial({
      color: c.wireframe,
      wireframe: true,
      transparent: true,
      opacity: level >= 3 ? 0.08 : 0.18,
    }),
  )
}

// ── Orbital ring (Lv 4) ───────────────────────────────────────────────────────
function _makeRing() {
  const geo = new THREE.TorusGeometry(1.9, 0.04, 8, 80)
  const mat = new THREE.MeshBasicMaterial({
    color: LEVEL_COLORS[4].wireframe,
    transparent: true,
    opacity: 0.6,
  })
  const ring = new THREE.Mesh(geo, mat)
  ring.rotation.x = Math.PI / 3
  return ring
}

// ── Particle halo (Lv 3+) ─────────────────────────────────────────────────────
function _makeParticles(level) {
  const count = level >= 4 ? 300 : 150
  const radius = level >= 4 ? 2.5 : 2.0
  const positions = new Float32Array(count * 3)

  for (let i = 0; i < count; i++) {
    const theta = Math.random() * Math.PI * 2
    const phi = Math.acos(2 * Math.random() - 1)
    const r = radius * (0.85 + Math.random() * 0.3)
    positions[i * 3 + 0] = r * Math.sin(phi) * Math.cos(theta)
    positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta)
    positions[i * 3 + 2] = r * Math.cos(phi)
  }

  const geo = new THREE.BufferGeometry()
  geo.setAttribute('position', new THREE.BufferAttribute(positions, 3))
  const mat = new THREE.PointsMaterial({
    color: LEVEL_COLORS[level]?.wireframe ?? 0xffffff,
    size: 0.04,
    transparent: true,
    opacity: 0.6,
    sizeAttenuation: true,
  })
  return new THREE.Points(geo, mat)
}

// ── Public: create crystal ────────────────────────────────────────────────────
export function createCrystal(level = 1) {
  // Clean up previous meshes
  if (_group) {
    scene.remove(_group)
    _group.traverse((child) => {
      child.geometry?.dispose()
      child.material?.dispose()
    })
  }

  _group = new THREE.Group()

  const geo = _makeGeometry(level)
  _coreMesh = new THREE.Mesh(geo, _makeMaterial(level))
  _coreMesh.castShadow = true
  _coreMesh.receiveShadow = true

  // Wireframe slightly larger to avoid z-fighting
  const wireGeo = geo.clone()
  _wireMesh = _makeWireframe(wireGeo, level)
  _wireMesh.scale.setScalar(1.02)

  _group.add(_coreMesh, _wireMesh)

  // Particles — Lv 3+
  if (level >= 3) {
    _particles = _makeParticles(level)
    _group.add(_particles)
  }

  // Orbital ring — Lv 4
  if (level >= 4) {
    _ring = _makeRing()
    _group.add(_ring)
  }

  scene.add(_group)
  return { update, pulse }
}

// ── Public: per-frame update ──────────────────────────────────────────────────
export function update(t) {
  if (!_group) return

  // Base rotation
  _group.rotation.y = t * 0.18
  _group.rotation.x = Math.sin(t * 0.11) * 0.08

  // Breathing scale
  const breathe = 1 + Math.sin(t * 1.4) * 0.018
  _group.scale.setScalar(breathe * _pulseScale(t))

  // Particles slowly counter-rotate
  if (_particles) {
    _particles.rotation.y = -t * 0.06
    _particles.rotation.x = t * 0.03
  }

  // Ring orbit
  if (_ring) {
    _ring.rotation.z = t * 0.22
  }
}

// ── Public: pulse animation ───────────────────────────────────────────────────
export function pulse() {
  _pulseT = performance.now() * 0.001
}

function _pulseScale(t) {
  const elapsed = t - _pulseT
  if (elapsed < 0 || elapsed > PULSE_DURATION) return 1

  // Quick spike then ease back: overshoot curve
  const p = elapsed / PULSE_DURATION
  return 1 + 0.28 * Math.sin(p * Math.PI) * (1 - p * 0.5)
}
